// Blog entries come from the build-time trimmed JSON, NOT contentlayer:
// this module is imported by the sitewide header (a client component), and
// importing allPosts here shipped the full ~7MB corpus (compiled MDX
// bodies) in every page's client bundle. blog-search-items.json is emitted
// by scripts/build-blog-dates.mjs after `npm run contentlayer` and carries
// only the five fields search needs (~120KB raw).
import blogSearchItems from './blog-search-items.json'
import { worksData } from '@/data/works'
import { servicesData } from '@/data/services'

export interface SearchableItem {
  id: string
  title: string
  description: string
  category: 'blog' | 'project' | 'service' | 'page'
  url: string
  tags?: string[]
}

export function buildSearchIndex(): SearchableItem[] {
  const items: SearchableItem[] = []

  // Index blog posts (pre-trimmed at build time — see import note above)
  items.push(...(blogSearchItems as SearchableItem[]))

  // Index projects
  worksData.forEach(work => {
    items.push({
      id: `project-${work.slug}`,
      title: work.title,
      description: work.description,
      category: 'project',
      url: work.slug,
      tags: work.technologies || [],
    })
  })

  // Index services
  Object.entries(servicesData).forEach(([slug, service]) => {
    items.push({
      id: `service-${slug}`,
      title: service.title,
      description: service.description,
      category: 'service',
      url: `/services/${slug}`,
      tags: [],
    })
  })

  // Partner delivery is a distinct agency channel, not a fifth direct-client
  // entry in servicesData, whose slugs all resolve beneath /services.
  items.push({
    id: 'service-agency-partner-delivery',
    title: 'Consulting',
    description:
      'White-label website, application, and AI visibility delivery for agencies and consultancies.',
    category: 'service',
    url: '/consulting',
    tags: [
      'white-label',
      'agency partner',
      'agency consulting',
      'consulting',
      'website delivery',
      'AI visibility',
    ],
  })

  // Index static pages
  const staticPages: SearchableItem[] = [
    {
      id: 'page-home',
      title: 'Home',
      description: 'AI-native design agency transforming ideas into revenue',
      category: 'page',
      url: '/',
    },
    {
      id: 'page-about',
      title: 'About',
      description: 'Learn about our team and mission',
      category: 'page',
      url: '/about',
    },
    {
      id: 'page-contact',
      title: 'Contact',
      description: 'Get in touch with us',
      category: 'page',
      url: '/contact-us',
    },
  ]

  items.push(...staticPages)
  return items
}

export function getCategoryLabel(category: SearchableItem['category']): string {
  const labels = {
    blog: 'Blog',
    project: 'Project',
    service: 'Service',
    page: 'Page',
  }
  return labels[category]
}
